import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, MessageCircle, ShieldCheck } from "lucide-react";
import { BRAND } from "../data/mock";
import { STATES } from "../data/shopMenu";
import { useAccount } from "../store/AccountContext";
import { fmtAddress } from "../components/store/AddressBook";
import { toast } from "sonner";
import { STORE_API, useCart, imgUrl, inr } from "../store/CartContext";
import { FreeShippingBar } from "../components/store/CartDrawer";

const inputCls = "w-full rounded-xl border border-[#3915AA]/15 bg-white px-3.5 py-3 text-sm text-[#1E0A5C] placeholder-[#1E0A5C]/40 outline-none focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106]";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

function Field({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#3915AA]/70">{label}</span>{children}</label>;
}

export default function Checkout() {
  const { items, subtotal, shipping, total, mrpTotal, clear, settings } = useCart();
  const online = settings.payments_enabled === true;
  const loadingPay = settings.payments_enabled === null;
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "Odisha", pincode: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const { user, api } = useAccount();
  const [saved, setSaved] = useState([]);
  const [picked, setPicked] = useState(null);
  const [saveAddr, setSaveAddr] = useState(true);
  const fill = (a) => { setPicked(a.id || "last"); setF((p) => ({ ...p, name: a.name || p.name, phone: a.phone || p.phone, line1: a.line1, line2: a.line2 || "", city: a.city, state: a.state, pincode: a.pincode })); };
  useEffect(() => {
    if (!user) return;
    setF((p) => ({ ...p, email: p.email || user.email, name: p.name || user.name || "" }));
    api.get("/addresses").then(({ data }) => {
      setSaved(data);
      const last = user.last_address;
      const lastSaved = last && data.find((a) => a.line1 === last.line1 && a.pincode === last.pincode);
      const def = lastSaved || last || data.find((a) => a.is_default) || data[0];
      if (def) fill(def);
      if (last && !lastSaved) setPicked(null);
      setSaveAddr(!data.length);
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const rememberAddress = async () => {
    if (!user) return;
    const addr = { name: f.name, phone: f.phone, line1: f.line1, line2: f.line2, city: f.city, state: f.state, pincode: f.pincode };
    const calls = [api.put("/last-address", addr)];
    if (saveAddr && !picked) calls.push(api.post("/addresses", { ...addr, label: "Home", is_default: !saved.length }));
    await Promise.race([Promise.allSettled(calls), new Promise((r) => setTimeout(r, 2500))]);
  };
  const up = (k) => (e) => { if (["line1", "line2", "city", "state", "pincode"].includes(k)) setPicked(null); setF((p) => ({ ...p, [k]: e.target.value })); };

  useEffect(() => {
    if (items.length) window.gtag?.("event", "begin_checkout", { currency: "INR", value: total });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!items.length) {
    return (
      <div data-testid="checkout-empty" className="min-h-[60vh] bg-[#F7F5FF] py-24 text-center text-[#1E0A5C]">
        <p className="font-heading text-2xl font-bold">Your cart is empty</p>
        <Link to="/shop" className="mt-4 inline-block rounded-full bg-[#FC5106] px-6 py-3 text-sm font-bold text-white">Continue shopping</Link>
      </div>
    );
  }

  const pay = async (e) => {
    e.preventDefault();
    if (f.phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit phone number");
    if (!/^[1-9][0-9]{5}$/.test(f.pincode)) return toast.error("Enter a valid 6-digit pincode");
    if (!online) {
      await rememberAddress();
      const lines = items.map((i) => `• ${i.name} (${i.variant_label}) × ${i.qty} = ${inr(i.price * i.qty)}`).join("\n");
      const msg = `Hi BarknFurs! I'd like to order:\n${lines}\nDelivery: ${shipping ? inr(shipping) : "FREE"}\nTotal: ${inr(total)}\n\nName: ${f.name}\nPhone: ${f.phone}\nAddress: ${f.line1}${f.line2 ? ", " + f.line2 : ""}, ${f.city}, ${f.state} - ${f.pincode}`;
      window.gtag?.("event", "generate_lead", { event_label: "shop_whatsapp_order", value: total });
      clear();
      window.location.href = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`;
      return;
    }
    setBusy(true);
    try {
      rememberAddress();
      const { data: order } = await axios.post(`${STORE_API}/orders`, {
        name: f.name, phone: f.phone, email: f.email || null, notes: f.notes,
        address: { line1: f.line1, line2: f.line2, city: f.city, state: f.state, pincode: f.pincode },
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
      });
      if (!(await loadRazorpay())) throw new Error("Could not load payment window. Check your connection.");
      const phone = f.phone.replace(/\D/g, "").slice(-10);
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR", order_id: order.razorpay_order_id,
        name: "BarknFurs Pet Shop", description: `Order ${order.order_number}`, image: `${window.location.origin}/favicon.png`,
        prefill: { name: f.name, email: f.email, contact: phone }, theme: { color: "#3915AA" },
        handler: async (res) => {
          try {
            await axios.post(`${STORE_API}/orders/${order.order_id}/verify`, res);
            window.gtag?.("event", "purchase", { transaction_id: order.order_number, currency: "INR", value: order.amount / 100 });
            clear();
            navigate(`/order/${order.order_number}?phone=${phone}`);
          } catch {
            toast.error("Payment received but verification failed. Please contact us with your order number.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed. Please try again."); setBusy(false); });
      rzp.open();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err.message || "Could not place order");
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-[#F7F5FF] text-[#1E0A5C]">
      <section className="container-custom grid gap-8 py-10 lg:grid-cols-[1fr_400px]">
        <form id="checkout-form" onSubmit={pay} className="space-y-6">
          <h1 className="font-heading text-3xl font-bold">Checkout</h1>
          <div className="space-y-4 rounded-3xl bg-white p-5 sm:p-7">
            <p className="font-heading text-lg font-bold">Contact</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name *"><input required value={f.name} onChange={up("name")} data-testid="checkout-name-input" className={inputCls} /></Field>
              <Field label="Phone *"><input required value={f.phone} onChange={up("phone")} inputMode="tel" data-testid="checkout-phone-input" className={inputCls} placeholder="10-digit mobile" /></Field>
              <Field label="Email (for receipt)"><input type="email" value={f.email} onChange={up("email")} data-testid="checkout-email-input" className={inputCls} /></Field>
            </div>
          </div>
          <div className="space-y-4 rounded-3xl bg-white p-5 sm:p-7">
            <p className="font-heading text-lg font-bold">Delivery address</p>
            {saved.length > 0 && (
              <div data-testid="checkout-saved-addresses" className="grid gap-2 sm:grid-cols-2">
                {saved.map((a) => (
                  <button type="button" key={a.id} onClick={() => fill(a)} data-testid={`checkout-address-${a.id}`} className={`rounded-xl border-2 p-3 text-left text-xs ${picked === a.id ? "border-[#FC5106] bg-[#FC5106]/5" : "border-[#3915AA]/10"}`}>
                    <span className="block text-sm font-bold">{a.label}{a.is_default ? " · Default" : ""}</span>{a.name} · {a.phone}<br /><span className="text-[#5E5675]">{fmtAddress(a)}</span>
                  </button>
                ))}
              </div>
            )}
            <Field label="House / flat, street *"><input required value={f.line1} onChange={up("line1")} data-testid="checkout-address-input" className={inputCls} /></Field>
            <Field label="Area, landmark"><input value={f.line2} onChange={up("line2")} data-testid="checkout-address2-input" className={inputCls} /></Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="City *"><input required value={f.city} onChange={up("city")} data-testid="checkout-city-input" className={inputCls} /></Field>
              <Field label="State *">
                <select value={f.state} onChange={up("state")} data-testid="checkout-state-input" className={inputCls}>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
              </Field>
              <Field label="Pincode *"><input required value={f.pincode} onChange={(e) => setF((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))} inputMode="numeric" data-testid="checkout-pincode-input" className={inputCls} /></Field>
            </div>
            {user && !picked && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} data-testid="checkout-save-address" className="h-4 w-4 accent-[#FC5106]" /> Save this address to my account</label>}
            <Field label="Order notes"><textarea rows={2} value={f.notes} onChange={up("notes")} data-testid="checkout-notes-input" className={`${inputCls} resize-none`} /></Field>
          </div>
        </form>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="space-y-4 rounded-3xl bg-white p-5 sm:p-6">
            <p className="font-heading text-lg font-bold">Order summary</p>
            <div className="max-h-72 space-y-3 overflow-y-auto">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3 text-sm">
                  <div className="relative"><img src={imgUrl(i.image)} alt="" className="h-14 w-14 rounded-lg object-cover" /><span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#3915AA] text-[10px] font-bold text-white">{i.qty}</span></div>
                  <div className="min-w-0 flex-1"><p className="line-clamp-1 font-semibold">{i.name}</p><p className="text-xs text-[#5E5675]">{i.variant_label}</p></div>
                  <p className="font-semibold">{inr(i.price * i.qty)}</p>
                </div>
              ))}
            </div>
            <FreeShippingBar />
            <div className="space-y-2 border-t border-[#3915AA]/10 pt-4 text-sm">
              <div className="flex justify-between"><span className="text-[#5E5675]">Subtotal</span><span>{inr(subtotal)}</span></div>
              {mrpTotal > subtotal && <div className="flex justify-between text-green-600"><span>Discount on MRP</span><span>−{inr(mrpTotal - subtotal)}</span></div>}
              <div className="flex justify-between"><span className="text-[#5E5675]">Delivery</span><span>{shipping ? inr(shipping) : "FREE"}</span></div>
              <div className="flex justify-between pt-2 text-lg font-bold"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
            </div>
            <button type="submit" form="checkout-form" disabled={busy || loadingPay} data-testid="checkout-pay-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FC5106] py-4 text-sm font-bold text-white hover:bg-[#E24804] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : online ? <Lock className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />} {online ? `Pay ${inr(total)} securely` : `Order on WhatsApp · ${inr(total)}`}
            </button>
            <p data-testid="checkout-pay-note" className="flex items-center justify-center gap-1.5 text-center text-[11px] text-[#5E5675]"><ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" /> {online ? "UPI, cards, netbanking & wallets via Razorpay" : "Online payment is coming soon — we'll confirm your order and payment on WhatsApp"}</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
